// Mithril global.

/**
 * A Text block's content as Mithril vnodes, with a safe subset of HTML honoured.
 *
 * Admins expect `<h3>`, `<a>`, `<img>` and lists to render, but an admin must
 * not be able to break their own site, so this is NOT innerHTML or m.trust.
 * DOMParser builds an inert document (nothing in it loads or runs); its tree is
 * walked and every allowed element rebuilt as a vnode with only the attributes
 * listed here. Anything else is dropped:
 *
 *   - script, style, iframe, form and the like go with their contents
 *   - any other unknown tag is unwrapped: its text and children stay
 *   - style, on* handlers and every unlisted attribute are never copied
 *   - href/src must be http(s):, mailto: (links) or a site-relative path
 *   - class keeps only Font Awesome tokens, so icons work but the forum's own
 *     classes (modals, fixed headers) can't be borrowed to cover the page
 *
 * Plain text with no block tags keeps its line breaks.
 */

const TAGS = new Set(['p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'b', 'em', 'i', 'u', 's', 'small', 'mark', 'sub', 'sup', 'code', 'pre', 'kbd', 'blockquote', 'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'a', 'img', 'span', 'div', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'details', 'summary']);

/** Dropped together with everything inside them. */
const DROP = new Set(['script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'template', 'noscript', 'svg', 'math', 'form', 'input', 'button', 'textarea', 'select', 'option', 'link', 'meta', 'base', 'title', 'head', 'audio', 'video', 'source', 'track', 'canvas', 'dialog', 'portal']);
const BLOCK = /<\s*(p|h[1-6]|ul|ol|li|div|blockquote|pre|table|hr|br|figure|dl|details)\b/i;
function safeUrl(raw, allowMail) {
  const url = (raw || '').trim();
  if (!url) return null;
  // Site-relative ("/t/news", "#top") but not protocol-relative ("//evil.example").
  if (url.startsWith('/') && !url.startsWith('//') || url.startsWith('#')) return url;
  return (allowMail ? /^(https?:|mailto:)/i : /^https?:/i).test(url) ? url : null;
}
const num = v => v && /^\d{1,4}$/.test(v) ? v : undefined;
function attrs(el) {
  const tag = el.tagName.toLowerCase();
  const out = {};
  const title = el.getAttribute('title');
  if (title) out.title = title;
  const fa = (el.getAttribute('class') || '').split(/\s+/).filter(c => /^fa[a-z]*(-[\w-]+)?$/.test(c));
  if (fa.length) out.className = fa.join(' ');
  if (tag === 'a') {
    const href = safeUrl(el.getAttribute('href'), true);
    if (href) out.href = href;
    if (el.getAttribute('target') === '_blank') {
      out.target = '_blank';
      out.rel = 'noopener noreferrer';
    }
  } else if (tag === 'img') {
    const src = safeUrl(el.getAttribute('src'), false);
    if (!src) return {
      skip: true
    };
    out.src = src;
    out.alt = el.getAttribute('alt') || '';
    out.loading = 'lazy';
    out.width = num(el.getAttribute('width'));
    out.height = num(el.getAttribute('height'));
  } else if (tag === 'td' || tag === 'th') {
    out.colspan = num(el.getAttribute('colspan'));
    out.rowspan = num(el.getAttribute('rowspan'));
  } else if (tag === 'ol') {
    out.start = num(el.getAttribute('start'));
  } else if (tag === 'details' && el.hasAttribute('open')) {
    out.open = true;
  }
  return out;
}

/** A sanitized node: text, or an allowed tag with its vetted attributes. */

function walk(nodes, keepBreaks) {
  const out = [];
  nodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!keepBreaks) return void out.push(text);
      text.split('\n').forEach((line, i) => {
        if (i) out.push({
          tag: 'br',
          attrs: {}
        });
        if (line) out.push(line);
      });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node;
    const tag = el.tagName.toLowerCase();
    if (DROP.has(tag)) return;
    if (!TAGS.has(tag)) return void out.push(...walk(el.childNodes, keepBreaks));
    const a = attrs(el);
    if (a.skip) return;
    const empty = tag === 'br' || tag === 'hr' || tag === 'img';
    out.push({
      tag,
      attrs: a,
      children: empty ? undefined : walk(el.childNodes, keepBreaks && tag !== 'pre')
    });
  });
  return out;
}

// Vnodes are fresh on every render (Mithril can't reuse them).
const toVnodes = nodes => nodes.map(n => typeof n === 'string' ? n : m(n.tag, n.attrs, n.children && toVnodes(n.children)));

// The block is re-rendered on every redraw (see renderZones), so parse once per
// distinct content rather than every frame.
const cache = new Map();
export function safeHtml(source) {
  const src = typeof source === 'string' ? source : '';
  if (!src) return [];
  let tree = cache.get(src);
  if (!tree) {
    tree = walk(new DOMParser().parseFromString(src, 'text/html').body.childNodes, !BLOCK.test(src));
    if (cache.size > 50) cache.clear();
    cache.set(src, tree);
  }
  return toVnodes(tree);
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/util/safeHtml', { safeHtml: safeHtml, });